import React from 'react'
import { useCompactViewport } from '~/hooks/useCompactViewport'
import { isUnlimited as checkUnlimited } from '~/utils/quotaConstants'
import { cm } from '~/utils/utils'
import { Typography } from '../common/Typography'
import type { CreditsData } from './types'

/**
 * CreditsCard Component
 *
 * Displays credit usage and billable flows in separate cards based on the billing model.
 *
 * ## Team vs Personal Context
 *
 * - **Team** (e.g., Dashboard): In billing period mode, show limit and denominator.
 *   Pass showLimits=true when timeRangeMode is 'billing_period'.
 * - **Personal** (e.g., PersonalUsageDialog / My Usage): Pass showLimits=false for denominators.
 *   Use `variant="billableFlowsOnly"` / `variant="creditsOnly"` to render a single card for stacked layouts.
 *
 * ## Billable Flow Display Rules
 *
 * When isBillableFlowPlan is true:
 *
 * - Credit Usage → omitted for normal users (no card). Super admins see actual credit usage
 *   with a short footnote at the bottom of the card (customers never see these totals).
 * - Billable flow usage → number only (personal context or workspace-filtered)
 * - Team billable flow usage → number / limit (team dashboard, all workspaces, billing period)
 *
 * The Billable Flows card shows "number" or "number / limit" depending on
 * showLimits && isAllWorkspacesSelected.
 *
 * ## Display Logic by Plan Type
 *
 * | Plan Type           | Credit Usage Card      | Billable Flows Card |
 * |---------------------|------------------------|---------------------|
 * | Quota-based         | Shows actual usage     | NOT shown           |
 * | Billable Flow       | Hidden for non–super-admins; super admin sees credits + footnote | Shows usage |
 *
 * ## Card Structure
 * - **Billable Flows card** (only for billable flow plans): shown first when present
 * - **Credit Usage card**: quota plans only see this; billable-flow plans show it after
 *   flows when `isSuperAdmin` (extra / support-only). Stacking order keeps customer-primary
 *   metrics on top.
 *
 * ## Billable Flow Plan Detection
 * A plan is considered a billable flow plan if:
 * 1. `isBillableFlowPlan` prop is explicitly set to true, OR
 * 2. There's an explicit billable flows limit set in the data (> 0)
 *
 * For non–super-admins the credit card is omitted on billable-flow plans. Super admins
 * still see credit usage figures for support/debugging.
 *
 * ## Denominator Display Rules
 *
 * The denominator (e.g., "/ 1,000" or "/ Unlimited"), progress bar, and remaining
 * count are ONLY shown when ALL of the following conditions are met:
 *
 * 1. **showLimits=true** (Billing Period mode, Team context)
 *    - Limits are tied to billing periods and reset each period
 *    - In Date Range mode, we're viewing an arbitrary time window that may span
 *      multiple billing periods, so showing a limit would be misleading
 *    - Personal usage view always passes showLimits=false
 *
 * 2. **isAllWorkspacesSelected=true** (viewing all workspaces)
 *    - Both credit and billable flow limits are account-level, not workspace-specific
 *    - Showing a limit for a filtered workspace view would be misleading since
 *      the limit applies to usage across ALL workspaces
 *
 * ## Display Matrix
 *
 * | Mode            | All Workspaces | Specific Workspace |
 * |-----------------|----------------|-------------------|
 * | Billing Period  | Usage + Limit  | Usage only        |
 * | Date Range      | Usage only     | Usage only        |
 * | Personal        | Usage only     | Usage only        |
 */
interface CreditsCardProps {
  data: CreditsData
  className?: string
  /**
   * Controls whether to show denominators (limits), progress bars, and remaining counts.
   *
   * When true (Billing Period mode):
   * - Shows denominator (e.g., "500 / 1,000" or "0 / Unlimited")
   * - Shows progress bar for usage visualization
   * - Shows "X usage" and "Y remaining" breakdown
   *
   * When false (Date Range mode):
   * - Only shows usage number (e.g., "500")
   * - Hides denominator, progress bar, and remaining count
   * - This is because limits are tied to billing periods, not arbitrary date ranges
   *
   * @default true
   */
  showLimits?: boolean
  /**
   * Controls workspace-aware display for both credits and billable flows.
   *
   * When true (viewing all workspaces):
   * - Shows denominator, progress bar, and remaining count (if showLimits is also true)
   *
   * When false (specific workspace selected):
   * - Only shows the usage number without limits/denominator
   * - Hides progress bar and remaining count
   *
   * This applies to BOTH credits and billable flows because both are account-level
   * metrics, not workspace-specific. Showing limits for a filtered workspace view
   * would be misleading since the limit applies to the entire account.
   *
   * @default true
   */
  isAllWorkspacesSelected?: boolean
  /**
   * Determines which billing model to display.
   *
   * When true (Billable Flow plan):
   * - Credit Usage card is omitted for non–super-admins; super admins see usage + footnote
   * - Billable Flows card is shown
   *
   * When false (Quota-based plan):
   * - Credit Usage shows actual usage
   * - Billable Flows card is NOT shown (even if there's data)
   *
   * @default false
   */
  isBillableFlowPlan?: boolean
  /**
   * Custom title for the main credits section.
   * @default "Credit Usage"
   */
  creditsSectionTitle?: string
  /**
   * Custom title for the billable flows card.
   * Use "Team Billable Flows" in the team dashboard (Manage Account).
   * @default "Billable Flows"
   */
  billableFlowsTitle?: string
  /**
   * Called when the user clicks on the credits/flows card to view details.
   */
  onDetailsClick?: () => void
  /**
   * When set with `showManageAccountLink`, renders a "Manage Account" control in the top-right
   * of the credits card (`creditsOnly` variant only; e.g. Team Credit Usage in My Usage).
   */
  onManageAccountClick?: () => void
  /**
   * @default false
   */
  showManageAccountLink?: boolean
  /**
   * When true on a billable-flow plan, the credit usage card shows actual usage numbers
   * (with a short “super admin only” footnote at the bottom of the card).
   * When false, the credit card is hidden for billable-flow plans.
   * @default false
   */
  isSuperAdmin?: boolean
  /**
   * `default` — billable-flow vs credit mutual exclusion (Manage Account dashboard).
   * `creditsOnly` / `billableFlowsOnly` — render a single card (My Usage stacked layout).
   */
  variant?: 'default' | 'creditsOnly' | 'billableFlowsOnly'
}

export const CreditsCard: React.FC<CreditsCardProps> = ({
  data,
  className,
  showLimits = true,
  isAllWorkspacesSelected = true,
  isBillableFlowPlan: isBillableFlowPlanProp = false,
  creditsSectionTitle,
  billableFlowsTitle = 'Billable Flows',
  onDetailsClick,
  onManageAccountClick,
  showManageAccountLink = false,
  isSuperAdmin = false,
  variant = 'default'
}) => {
  const hasExplicitBillableFlowsLimit =
    data.billable_flows_limit != null && data.billable_flows_limit > 0
  const isBillableFlowPlan = isBillableFlowPlanProp || !!data.is_billable_flow_plan

  // Check for unlimited using centralized utility
  const isUnlimited = checkUnlimited(data.total)
  // Show denominator/progress bar only when in billing period mode AND viewing all workspaces
  // Credit limits are team-level, so showing them for specific workspaces is misleading
  const hasTotal = data.total != null && showLimits && isAllWorkspacesSelected

  // Credits used in billing period
  const creditsUsed = data.used

  // Calculate percentage for progress bar
  const usedPercentage = !hasTotal || isUnlimited ? 0 : (creditsUsed / (data.total ?? 1)) * 100

  // Determine the title for the main credits section
  const title = creditsSectionTitle ?? 'Credit Usage'

  // Billable flows display
  const billableFlowsUsed = data.billable_flows_used ?? 0
  // Only calculate remaining/percentage when there's an explicit limit
  const billableFlowsLimit = hasExplicitBillableFlowsLimit ? data.billable_flows_limit! : 0
  const billableFlowsPercentage = hasExplicitBillableFlowsLimit
    ? (billableFlowsUsed / billableFlowsLimit) * 100
    : 0

  const isCompactViewport = useCompactViewport()

  // Desktop sub-card height is fixed at 127px (≈ H/2 with H = 269 and gap-4).
  // Paired with min-h-[269px] on UsageOverviewCard, a 2-card stack is
  // 2*127+16 = 270px (1px slack from gap, negligible). 1 card leaves space
  // below the stack; 3+ cards stretch the Overview via default flex stretch.
  // Sub-card content (incl. the super-admin footnote, which is intentionally
  // kept short to fit one line) stays under 127px. Callers that want a
  // different height can override by passing `h-...` in className.
  const cardClassName = cm(
    'bg-tokens-neutral-fill-lighten-30 border border-tokens-neutral-bg-white rounded-2xl flex flex-col',
    isCompactViewport ? 'px-4 py-4' : 'px-8 py-6',
    !className?.includes('w-') && !isCompactViewport && 'w-[367px]',
    !className?.includes('w-') && isCompactViewport && 'w-full',
    !className?.includes('h-') && !isCompactViewport && 'h-[127px]'
  )

  // Credit Usage sub-card: title, used/limit, optional progress bar, optional
  // "Manage Account" header link. On billable-flow plans, shown only to super
  // admins (with a footnote). Used by variant='default' and 'creditsOnly'.
  const renderCreditsCard = () => {
    if (variant === 'creditsOnly' && isBillableFlowPlan && !isSuperAdmin) {
      return null
    }

    // Footnote is intentionally short so it fits one line at labelSm — keeps
    // the whole card under the fixed h-[127px] (see cardClassName comment).
    // If you lengthen it, also bump card height (and Overview min-height).
    const superAdminFootnote =
      isBillableFlowPlan && isSuperAdmin ? (
        <Typography
          fontFamily='geist'
          fontSize='labelSm'
          fontWeight='normal'
          className='text-tokens-neutral-text-tertiary select-none leading-tight line-clamp-1 shrink-0 pt-1'
        >
          Super admin view — hidden from customers.
        </Typography>
      ) : null

    // mt-auto pins the progress bar to the bottom of the card (the card has a
    // fixed H/2 height on desktop, so extra space flows above the bar).
    const progressTopSpacing = 'mt-auto'

    const hasManageAccountHeader =
      variant === 'creditsOnly' && Boolean(showManageAccountLink && onManageAccountClick)

    const resolvedCreditsCardClassName = cm(
      'bg-tokens-neutral-fill-lighten-30 border border-tokens-neutral-bg-white rounded-2xl flex flex-col',
      isCompactViewport ? 'px-4 py-4' : 'px-8 py-6',
      !className?.includes('w-') && !isCompactViewport && 'w-[367px]',
      !className?.includes('w-') && isCompactViewport && 'w-full',
      // Fixed H/2 height on desktop (see cardClassName above for rationale).
      !className?.includes('h-') && !isCompactViewport && 'h-[127px]',
      className
    )

    return (
      <div className={resolvedCreditsCardClassName}>
        <div className='flex flex-1 flex-col gap-1'>
          {hasManageAccountHeader ? (
            <div className='flex items-start justify-between gap-3 mb-1 shrink-0'>
              <Typography
                fontFamily='geist'
                fontSize='bodySm'
                fontWeight='medium'
                className='text-tokens-primary-1 select-none min-w-0'
              >
                {title}
              </Typography>
              <button
                type='button'
                onClick={e => {
                  e.stopPropagation()
                  onManageAccountClick?.()
                }}
                className='cursor-pointer hover:opacity-70 transition-opacity shrink-0 self-start'
              >
                <Typography
                  fontFamily='geist'
                  fontSize='bodySm'
                  fontWeight='medium'
                  className='text-tokens-status-link-1 select-none whitespace-nowrap'
                >
                  Manage Account
                </Typography>
              </button>
            </div>
          ) : (
            <Typography
              fontFamily='geist'
              fontSize='bodySm'
              fontWeight='medium'
              className='text-tokens-primary-1 select-none shrink-0'
            >
              {title}
            </Typography>
          )}
          <div className='flex items-baseline shrink-0'>
            <span data-testid='team-credit-usage-value'>
              <Typography
                fontFamily='geist'
                fontSize='titleLg'
                fontWeight='bold'
                className='text-tokens-primary-2 select-none'
              >
                {creditsUsed.toLocaleString()}
              </Typography>
            </span>
            {/* Only show denominator when viewing all workspaces in billing period mode */}
            {showLimits && isAllWorkspacesSelected && (
              <Typography
                fontFamily='geist'
                fontSize='bodyMd'
                fontWeight='normal'
                className='text-tokens-neutral-text-tertiary ml-1 select-none'
              >
                / {isUnlimited ? 'Unlimited' : (data.total ?? 0).toLocaleString()}
              </Typography>
            )}
          </div>

          {/* Super admin footnote — sits just below the value (in the spot
              the progress bar occupied before mt-auto). The bar itself
              drops to the bottom of the card below. */}
          {superAdminFootnote}

          {/* Progress Bar - purple = usage, gray = remaining.
              mt-auto pins it to the bottom of the fixed-height card. */}
          {hasTotal && !isUnlimited && (
            <div
              className={cm(
                progressTopSpacing,
                'w-full h-2 bg-tokens-neutral-fill-fill-6 rounded-full overflow-hidden flex shrink-0'
              )}
            >
              {creditsUsed > 0 && (
                <div
                  className='h-full bg-tokens-status-link-1 transition-all duration-300'
                  style={{ width: `${Math.min(100, usedPercentage)}%` }}
                />
              )}
            </div>
          )}
        </div>
      </div>
    )
  }

  // Billable Flows sub-card: title, used count, optional denominator + progress
  // bar, optional chevron (whole card becomes a button when onDetailsClick is
  // set). Used by variant='default' and 'billableFlowsOnly'.
  const renderBillableFlowsCard = () => {
    // Denominator/progress bar should only show when BOTH conditions are met:
    // 1. showLimits=true (Billing Period mode, not Date Range mode)
    // 2. isAllWorkspacesSelected=true (viewing all workspaces, not a specific one)
    // This is because limits are tied to billing periods and apply to the entire account
    const showBillableFlowsLimits = showLimits && isAllWorkspacesSelected

    return (
      <div
        className={cm(cardClassName, className, onDetailsClick && 'cursor-pointer relative')}
        onClick={onDetailsClick}
        role={onDetailsClick ? 'button' : undefined}
        tabIndex={onDetailsClick ? 0 : undefined}
        onKeyDown={
          onDetailsClick
            ? e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  onDetailsClick()
                }
              }
            : undefined
        }
      >
        <div className='flex flex-col gap-1'>
          <Typography
            fontFamily='geist'
            fontSize='bodySm'
            fontWeight='medium'
            className='text-tokens-primary-1 select-none'
          >
            {billableFlowsTitle}
          </Typography>
          <div className='flex items-baseline'>
            <Typography
              fontFamily='geist'
              fontSize='titleLg'
              fontWeight='bold'
              className='text-tokens-primary-2 select-none'
            >
              {billableFlowsUsed.toLocaleString()}
            </Typography>
            {/* Only show denominator in billing period mode AND viewing all workspaces */}
            {showBillableFlowsLimits && (
              <Typography
                fontFamily='geist'
                fontSize='bodyMd'
                fontWeight='normal'
                className='text-tokens-neutral-text-tertiary ml-1 select-none'
              >
                /{' '}
                {hasExplicitBillableFlowsLimit ? billableFlowsLimit.toLocaleString() : 'Unlimited'}
              </Typography>
            )}
          </div>
        </div>

        {/* Progress Bar - only shown in billing period mode, all workspaces, AND explicit limit.
            mt-auto pins the bar to the bottom of the fixed-height card. */}
        {showBillableFlowsLimits && hasExplicitBillableFlowsLimit && (
          <div className='mt-auto w-full h-2 bg-tokens-neutral-fill-fill-6 rounded-full overflow-hidden flex'>
            {billableFlowsUsed > 0 && (
              <div
                className='h-full bg-tokens-status-link-1 transition-all duration-300'
                style={{ width: `${Math.min(100, billableFlowsPercentage)}%` }}
              />
            )}
          </div>
        )}

        {/* Chevron button */}
        {onDetailsClick && (
          <svg
            className='absolute top-4 right-4 w-5 h-5 text-tokens-neutral-text-tertiary'
            viewBox='0 0 20 20'
            fill='currentColor'
          >
            <path
              fillRule='evenodd'
              d='M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z'
              clipRule='evenodd'
            />
          </svg>
        )}
      </div>
    )
  }

  // Variant dispatch:
  //   'creditsOnly'       — single Credit Usage sub-card, no outer wrapper.
  //   'billableFlowsOnly' — single Billable Flows sub-card, no outer wrapper.
  //   'default'           — both sub-cards stacked inside a flex-col wrapper.
  if (variant === 'creditsOnly') {
    return renderCreditsCard()
  }
  if (variant === 'billableFlowsOnly') {
    return renderBillableFlowsCard()
  }

  // Default variant: stacked two-card wrapper (Billable Flows on top, Credit
  // Usage below). Each sub-card is a fixed ≈H/2 (127px desktop), so 2 cards +
  // gap-4 = 270px, matching the Overview's 269px min-height (1px slack).
  // Wrapper sizes to its children; caller className flows through for width.
  // Guard tests in CreditsCard.test.tsx lock this in.
  //
  // Visibility: Billable Flows shows on billable-flow plans; Credit Usage
  // shows on credit plans always, and on billable-flow plans only to super
  // admins (with a one-line footnote sized to fit the 127px card).
  const wrapperClassName = cm('flex flex-col gap-4', className)
  return (
    <div className={wrapperClassName}>
      {isBillableFlowPlan && renderBillableFlowsCard()}
      {(!isBillableFlowPlan || isSuperAdmin) && renderCreditsCard()}
    </div>
  )
}

export default CreditsCard
